import { camelizeKeys } from "humps";
import pick from "lodash/pick";
import { Activity, ActivityOption, Id } from "PFTypes";
import { useEffect, useRef, useState } from "react";
import { UseFormSetValue } from "react-hook-form";
import { usePrevious, useUnmount } from "react-use";

import { BookingFormValues } from "../booking_form_values.types";

type UseEngagementSelectionStateParams = {
  activity: Activity | undefined;
  isInitialActivityEngagement: boolean | undefined;
  currentProfileId: Id;
  workforceMember: BookingFormValues["workforceMember"];
  setValue: UseFormSetValue<BookingFormValues>;
  onEngagementChange: (engagementOption: ActivityOption | null) => void;
};

export const useEngagementSelectionState = ({
  activity,
  isInitialActivityEngagement,
  currentProfileId,
  workforceMember,
  setValue,
  onEngagementChange
}: UseEngagementSelectionStateParams) => {
  const initialEngagementId = useRef<Id | undefined>(isInitialActivityEngagement ? activity?.id : undefined);
  const [selectedEngagement, setSelectedEngagement] = useState<ActivityOption | null>(null);
  const prevWorkforceMember = usePrevious(workforceMember);

  const handleChange = (engagementOption: ActivityOption | null, onChange: (value: any) => void) => {
    onEngagementChange(engagementOption);
    setSelectedEngagement(engagementOption);
    onChange(engagementOption?.id);
  };

  useEffect(() => {
    setSelectedEngagement(
      isInitialActivityEngagement
        ? (pick(
            camelizeKeys(activity),
            "id",
            "name",
            "description",
            "bookingCategoryId",
            "templateId"
          ) as ActivityOption)
        : null
    );
  }, [activity]);

  useEffect(() => {
    const prevSelectedProfileId =
      prevWorkforceMember && "id" in prevWorkforceMember ? prevWorkforceMember.id : undefined;
    const newSelectedProfileId = workforceMember && "id" in workforceMember ? workforceMember.id : undefined;
    if (prevSelectedProfileId === currentProfileId && newSelectedProfileId !== currentProfileId) {
      // activity is removed in case user changes from current profile to other
      // as there might be less available activities (ownerId vs contributorId filtering)
      setValue("activityId", undefined, { shouldValidate: true });
      setSelectedEngagement(null);
    }
  }, [workforceMember]);

  useUnmount(() => setValue("activityId", undefined));

  return { selectedEngagement, initialEngagementId, handleChange };
};
